// Minimal Supabase client that works in production
// This avoids all the complex initialization that causes the headers error

import { getRuntimeEnv, isEnvConfigured, debugEnv } from './env-config';

let supabaseClient: any = null;

export function getMinimalSupabase() {
  if (supabaseClient) return supabaseClient;
  
  // Get config with runtime fallback
  const config = getRuntimeEnv();
  const url = config.supabaseUrl;
  const key = config.supabaseAnonKey;
  
  if (!url || !key) {
    if (import.meta.env.DEV) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('❌ CRITICAL: Supabase credentials missing!');
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('URL:', url || 'NOT SET');
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Key:', key ? 'Set but invalid' : 'NOT SET');
        }

      }
    }
    debugEnv();
    
    // Show user-friendly error
    if (typeof window !== 'undefined' && window.location.pathname.includes('/portal')) {
      alert('Configuration Error: Unable to connect to authentication service. Please contact support.');
    }
    
    return null;
  }
  
  // Validate the key format (should be a JWT-like string)
  if (key && (key === 'test' || key.length < 100)) {
    if (import.meta.env.DEV) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('❌ CRITICAL: Invalid Supabase API key detected!');
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Key appears to be a test/placeholder value');
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Please ensure environment variables are properly configured in production');
        }

      }
    }
    debugEnv();
  }
  
  // Create a minimal client facade that works without the problematic SDK
  supabaseClient = {
    auth: {
      signInWithPassword: async ({ email, password }: { email: string; password: string }) => {
        try {
          // Remove sensitive logging in production
          const response = await fetch(`${url}/auth/v1/token?grant_type=password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': key
            },
            body: JSON.stringify({ 
              email, 
              password,
              gotrue_meta_security: {}
            })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            // Login failed - error details in response
            try {
              const error = JSON.parse(errorText);
              return { data: null, error: new Error(error.error_description || error.msg || error.error || 'Login failed') };
            } catch {
              return { data: null, error: new Error(`Login failed: ${response.status}`) };
            }
          }
          
          const data = await response.json();
          // Login successful
          
          // Store the session with timestamp and check if remember me was set
          if (data.access_token) {
            const rememberMe = window.localStorage.getItem('supabase.auth.rememberMe') === 'true';
            const storage = rememberMe ? localStorage : sessionStorage;
            
            // For admin users, always use sessionStorage for security
            const isAdmin = data.user?.role === 'super_admin' || data.user?.role === 'pcm_staff';
            const finalStorage = isAdmin ? sessionStorage : storage;
            
            finalStorage.setItem('supabase-auth-token', data.access_token);
            finalStorage.setItem('supabase-refresh-token', data.refresh_token);
            finalStorage.setItem('supabase-user', JSON.stringify(data.user));
            finalStorage.setItem('supabase-auth-timestamp', Date.now().toString());
            
            // Clear from the other storage to prevent conflicts
            if (finalStorage === sessionStorage) {
              localStorage.removeItem('supabase-auth-token');
              localStorage.removeItem('supabase-refresh-token');
              localStorage.removeItem('supabase-user');
              localStorage.removeItem('supabase-auth-timestamp');
            } else {
              sessionStorage.removeItem('supabase-auth-token');
              sessionStorage.removeItem('supabase-refresh-token');
              sessionStorage.removeItem('supabase-user');
              sessionStorage.removeItem('supabase-auth-timestamp');
            }
            
            // Dispatch custom event to notify auth state change within same window
            window.dispatchEvent(new CustomEvent('supabase:auth-state-change', {
              detail: { event: 'SIGNED_IN', session: { user: data.user, access_token: data.access_token } }
            }));
          }
          
          return { data: { user: data.user, session: data }, error: null };
        } catch (error) {
          return { data: null, error };
        }
      },
      
      signOut: async () => {
        // Clear from both storages to ensure complete logout
        localStorage.removeItem('supabase-auth-token');
        localStorage.removeItem('supabase-refresh-token');
        localStorage.removeItem('supabase-user');
        localStorage.removeItem('supabase-auth-timestamp');
        localStorage.removeItem('supabase.auth.rememberMe');
        
        sessionStorage.removeItem('supabase-auth-token');
        sessionStorage.removeItem('supabase-refresh-token');
        sessionStorage.removeItem('supabase-user');
        sessionStorage.removeItem('supabase-auth-timestamp');
        
        // Dispatch custom event to notify auth state change within same window
        window.dispatchEvent(new CustomEvent('supabase:auth-state-change', {
          detail: { event: 'SIGNED_OUT', session: null }
        }));
        
        return { error: null };
      },
      
      getSession: async () => {
        // Check both localStorage and sessionStorage
        let token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
        let userStr = localStorage.getItem('supabase-user') || sessionStorage.getItem('supabase-user');
        let timestamp = localStorage.getItem('supabase-auth-timestamp') || sessionStorage.getItem('supabase-auth-timestamp');
        
        if (!token || !userStr) {
          return { data: { session: null }, error: null };
        }
        
        try {
          const user = JSON.parse(userStr);
          
          // Check session expiry
          if (timestamp) {
            const sessionAge = Date.now() - parseInt(timestamp);
            const maxAge = user.role === 'super_admin' || user.role === 'pcm_staff' 
              ? 1000 * 60 * 60 * 2 // 2 hours for admin users
              : 1000 * 60 * 60 * 24 * 7; // 7 days for regular users
            
            if (sessionAge > maxAge) {
              // Session expired, clear it
              await supabaseClient.auth.signOut();
              return { data: { session: null }, error: null };
            }
          }
          
          return {
            data: {
              session: {
                access_token: token,
                user: user
              }
            },
            error: null
          };
        } catch {
          return { data: { session: null }, error: null };
        }
      },
      
      onAuthStateChange: (callback: any) => {
        // Check both localStorage and sessionStorage
        const checkAuth = async () => {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          const userStr = localStorage.getItem('supabase-user') || sessionStorage.getItem('supabase-user');
          const timestamp = localStorage.getItem('supabase-auth-timestamp') || sessionStorage.getItem('supabase-auth-timestamp');
          
          if (token && userStr) {
            try {
              const user = JSON.parse(userStr);
              
              // Check session expiry
              if (timestamp) {
                const sessionAge = Date.now() - parseInt(timestamp);
                const maxAge = user.role === 'super_admin' || user.role === 'pcm_staff' 
                  ? 1000 * 60 * 60 * 2 // 2 hours for admin users
                  : 1000 * 60 * 60 * 24 * 7; // 7 days for regular users
                
                if (sessionAge > maxAge) {
                  // Session expired, clear it
                  await supabaseClient.auth.signOut();
                  callback('SIGNED_OUT', null);
                  return;
                }
              }
              
              callback('SIGNED_IN', { user, access_token: token });
            } catch {
              callback('SIGNED_OUT', null);
            }
          } else {
            callback('SIGNED_OUT', null);
          }
        };
        
        // Check immediately
        checkAuth();
        
        // Listen for custom auth state change events (same window)
        const handleCustomAuthChange = (event: any) => {
          const { detail } = event;
          callback(detail.event, detail.session);
        };
        window.addEventListener('supabase:auth-state-change', handleCustomAuthChange);
        
        // Listen for storage changes (other windows/tabs)
        window.addEventListener('storage', checkAuth);
        
        return {
          data: {
            subscription: {
              unsubscribe: () => {
                window.removeEventListener('storage', checkAuth);
                window.removeEventListener('supabase:auth-state-change', handleCustomAuthChange);
              }
            }
          }
        };
      },
      
      resetPasswordForEmail: async (email: string, options?: any) => {
        try {
          const response = await fetch(`${url}/auth/v1/recover`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': key
            },
            body: JSON.stringify({ 
              email,
              redirect_to: options?.redirectTo 
            })
          });
          
          if (!response.ok) {
            const error = await response.json();
            return { error: new Error(error.msg || 'Failed to send reset email') };
          }
          
          return { error: null };
        } catch (error) {
          return { error };
        }
      },
      
      updateUser: async ({ password }: { password: string }) => {
        const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
        if (!token) {
          return { error: new Error('Not authenticated') };
        }
        
        try {
          const response = await fetch(`${url}/auth/v1/user`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'apikey': key,
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ password })
          });
          
          if (!response.ok) {
            const error = await response.json();
            return { error: new Error(error.msg || 'Failed to update password') };
          }
          
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { error };
        }
      },
      
      mfa: {
        enroll: async ({ factorType, friendlyName }: any) => {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          if (!token) {
            return { error: new Error('Not authenticated') };
          }
          
          try {
            const response = await fetch(`${url}/auth/v1/factors`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': key,
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ factor_type: factorType, friendly_name: friendlyName })
            });
            
            if (!response.ok) {
              const error = await response.json();
              return { error: new Error(error.msg || 'Failed to enroll MFA') };
            }
            
            const data = await response.json();
            return { data, error: null };
          } catch (error) {
            return { error };
          }
        },
        
        verify: async ({ factorId, code }: any) => {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          if (!token) {
            return { error: new Error('Not authenticated') };
          }
          
          try {
            const response = await fetch(`${url}/auth/v1/factors/${factorId}/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': key,
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ code })
            });
            
            if (!response.ok) {
              const error = await response.json();
              return { error: new Error(error.msg || 'Failed to verify MFA') };
            }
            
            const data = await response.json();
            return { data, error: null };
          } catch (error) {
            return { error };
          }
        },
        
        unenroll: async ({ factorId }: any) => {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          if (!token) {
            return { error: new Error('Not authenticated') };
          }
          
          try {
            const response = await fetch(`${url}/auth/v1/factors/${factorId}`, {
              method: 'DELETE',
              headers: {
                'apikey': key,
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!response.ok) {
              const error = await response.json();
              return { error: new Error(error.msg || 'Failed to unenroll MFA') };
            }
            
            return { error: null };
          } catch (error) {
            return { error };
          }
        },
        
        listFactors: async () => {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          if (!token) {
            return { error: new Error('Not authenticated') };
          }
          
          try {
            const response = await fetch(`${url}/auth/v1/factors`, {
              method: 'GET',
              headers: {
                'apikey': key,
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!response.ok) {
              const error = await response.json();
              return { data: null, error: new Error(error.msg || 'Failed to list MFA factors') };
            }
            
            const data = await response.json();
            return { data: { totp: data }, error: null };
          } catch (error) {
            return { data: null, error };
          }
        }
      },
      
      getUser: async () => {
        const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
        const userStr = localStorage.getItem('supabase-user') || sessionStorage.getItem('supabase-user');
        
        if (!token || !userStr) {
          return { data: { user: null }, error: null };
        }
        
        try {
          const user = JSON.parse(userStr);
          return { data: { user }, error: null };
        } catch {
          return { data: { user: null }, error: null };
        }
      },
      
      signUp: async ({ email, password, options }: any) => {
        try {
          const response = await fetch(`${url}/auth/v1/signup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': key
            },
            body: JSON.stringify({ 
              email, 
              password,
              data: options?.data
            })
          });
          
          if (!response.ok) {
            const error = await response.json();
            return { data: null, error: new Error(error.msg || 'Signup failed') };
          }
          
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { data: null, error };
        }
      }
    },
    
    from: (table: string) => {
      const filters: string[] = [];
      const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
      
      const buildUrl = () => {
        const baseUrl = `${url}/rest/v1/${table}`;
        if (filters.length === 0) return baseUrl;
        return `${baseUrl}?${filters.join('&')}`;
      };
      
      const executeQuery = async () => {
        try {
          const response = await fetch(buildUrl(), {
            headers: {
              'apikey': key,
              'Authorization': token ? `Bearer ${token}` : `Bearer ${key}`,
              'Accept': 'application/json',
              'Prefer': 'return=representation'
            }
          });
          
          if (!response.ok) {
            const error = await response.text();
            
            // Check for JWT expired error
            if (error.includes('JWT expired') || error.includes('PGRST301')) {
              // JWT expired, clearing session
              // Clear the expired session
              localStorage.removeItem('supabase-auth-token');
              localStorage.removeItem('supabase-refresh-token');
              localStorage.removeItem('supabase-user');
              localStorage.removeItem('supabase-auth-timestamp');
              sessionStorage.removeItem('supabase-auth-token');
              sessionStorage.removeItem('supabase-refresh-token');
              sessionStorage.removeItem('supabase-user');
              sessionStorage.removeItem('supabase-auth-timestamp');
              
              // Only redirect if we're not already on the login page
              if (!window.location.pathname.includes('/login')) {
                // Check if user is in client portal and redirect appropriately
                if (window.location.pathname.includes('/client-portal')) {
                  window.location.href = '/client-portal/login';
                } else {
                  window.location.href = '/login';
                }
              }
              return { data: null, error: new Error('Session expired. Please login again.') };
            }
            
            return { data: null, error: new Error(error) };
          }
          
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { data: null, error };
        }
      };
      
      const queryBuilder = {
        select: (columns = '*') => {
          // Clean up whitespace and encode the select parameter
          const cleanColumns = columns.replace(/\s+/g, ' ').trim();
          filters.push(`select=${encodeURIComponent(cleanColumns)}`);
          return queryBuilder;
        },
        eq: (column: string, value: any) => {
          filters.push(`${column}=eq.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        neq: (column: string, value: any) => {
          filters.push(`${column}=neq.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        gt: (column: string, value: any) => {
          filters.push(`${column}=gt.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        gte: (column: string, value: any) => {
          filters.push(`${column}=gte.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        lt: (column: string, value: any) => {
          filters.push(`${column}=lt.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        lte: (column: string, value: any) => {
          filters.push(`${column}=lte.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        like: (column: string, value: string) => {
          filters.push(`${column}=like.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        ilike: (column: string, value: string) => {
          filters.push(`${column}=ilike.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        is: (column: string, value: any) => {
          filters.push(`${column}=is.${encodeURIComponent(value)}`);
          return queryBuilder;
        },
        in: (column: string, values: any[]) => {
          const encodedValues = values.map(v => encodeURIComponent(v));
          filters.push(`${column}=in.(${encodedValues.join(',')})`);
          return queryBuilder;
        },
        order: (column: string, options?: { ascending?: boolean }) => {
          const direction = options?.ascending === false ? '.desc' : '.asc';
          filters.push(`order=${column}${direction}`);
          return queryBuilder;
        },
        limit: (count: number) => {
          filters.push(`limit=${count}`);
          return queryBuilder;
        },
        single: async () => {
          const result = await executeQuery();
          if (result.error) return result;
          return { data: result.data?.[0] || null, error: null };
        },
        then: async (resolve: any, reject: any) => {
          try {
            const result = await executeQuery();
            if (result.error) {
              reject?.(result.error);
            } else {
              resolve?.(result);
            }
          } catch (error) {
            reject?.(error);
          }
        },
        insert: (data: any | any[]) => {
          // Return a chainable builder for insert operations
          let selectColumns = '*';

          const executeInsert = async () => {
            try {
              const authToken = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');

              const response = await fetch(`${url}/rest/v1/${table}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'apikey': key,
                  'Authorization': authToken ? `Bearer ${authToken}` : `Bearer ${key}`,
                  'Accept': 'application/json',
                  'Prefer': 'return=representation',
                  ...(authToken ? { 'X-Client-Info': 'supabase-js/2.0.0' } : {})
                },
                body: JSON.stringify(data)
              });

              if (!response.ok) {
                const error = await response.text();
                try {
                  const errorObj = JSON.parse(error);
                  return { data: null, error: new Error(errorObj.message || errorObj.error || error) };
                } catch {
                  return { data: null, error: new Error(error) };
                }
              }

              const result = await response.json();
              return { data: result, error: null };
            } catch (error) {
              return { data: null, error };
            }
          };

          const insertBuilder = {
            select: (columns?: string) => {
              selectColumns = columns || '*';
              return insertBuilder;
            },
            single: () => {
              // Return a builder that resolves to single item
              return {
                then: async (resolve: any, reject: any) => {
                  try {
                    const result = await executeInsert();
                    if (result.data && Array.isArray(result.data)) {
                      resolve?.({ data: result.data[0] || null, error: result.error });
                    } else {
                      resolve?.(result);
                    }
                  } catch (error) {
                    reject?.(error);
                  }
                }
              };
            },
            then: async (resolve: any, reject: any) => {
              try {
                const result = await executeInsert();
                resolve?.(result);
              } catch (error) {
                reject?.(error);
              }
            }
          };
          return insertBuilder;
        },
        update: (data: any) => {
          // Return a chainable builder for update operations
          const executeUpdate = async () => {
            try {
              const authToken = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
              const response = await fetch(buildUrl(), {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'apikey': key,
                  'Authorization': authToken ? `Bearer ${authToken}` : `Bearer ${key}`,
                  'Accept': 'application/json',
                  'Prefer': 'return=representation'
                },
                body: JSON.stringify(data)
              });

              if (!response.ok) {
                const error = await response.text();
                return { data: null, error: new Error(error) };
              }

              const result = await response.json();
              return { data: result, error: null };
            } catch (error) {
              return { data: null, error };
            }
          };

          const updateBuilder = {
            eq: (column: string, value: any) => {
              filters.push(`${column}=eq.${encodeURIComponent(value)}`);
              return updateBuilder;
            },
            neq: (column: string, value: any) => {
              filters.push(`${column}=neq.${encodeURIComponent(value)}`);
              return updateBuilder;
            },
            in: (column: string, values: any[]) => {
              const encodedValues = values.map(v => encodeURIComponent(v));
              filters.push(`${column}=in.(${encodedValues.join(',')})`);
              return updateBuilder;
            },
            then: async (resolve: any, reject: any) => {
              try {
                const result = await executeUpdate();
                resolve?.(result);
              } catch (error) {
                reject?.(error);
              }
            }
          };
          return updateBuilder;
        },
        delete: () => {
          // Return a chainable builder for delete operations
          const executeDelete = async () => {
            try {
              const authToken = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
              const response = await fetch(buildUrl(), {
                method: 'DELETE',
                headers: {
                  'apikey': key,
                  'Authorization': authToken ? `Bearer ${authToken}` : `Bearer ${key}`,
                  'Accept': 'application/json',
                  'Prefer': 'return=representation'
                }
              });

              if (!response.ok) {
                const error = await response.text();
                return { data: null, error: new Error(error) };
              }

              const result = await response.json();
              return { data: result, error: null };
            } catch (error) {
              return { data: null, error };
            }
          };

          const deleteBuilder = {
            eq: (column: string, value: any) => {
              filters.push(`${column}=eq.${encodeURIComponent(value)}`);
              return deleteBuilder;
            },
            neq: (column: string, value: any) => {
              filters.push(`${column}=neq.${encodeURIComponent(value)}`);
              return deleteBuilder;
            },
            in: (column: string, values: any[]) => {
              const encodedValues = values.map(v => encodeURIComponent(v));
              filters.push(`${column}=in.(${encodedValues.join(',')})`);
              return deleteBuilder;
            },
            then: async (resolve: any, reject: any) => {
              try {
                const result = await executeDelete();
                resolve?.(result);
              } catch (error) {
                reject?.(error);
              }
            }
          };
          return deleteBuilder;
        }
      };
      
      return queryBuilder;
    },
    
    rpc: async (functionName: string, params?: any) => {
      const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
      
      try {
        const response = await fetch(`${url}/rest/v1/rpc/${functionName}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': key,
            'Authorization': token ? `Bearer ${token}` : `Bearer ${key}`,
            'Accept': 'application/json'
          },
          body: params ? JSON.stringify(params) : '{}'
        });
        
        if (!response.ok) {
          const error = await response.text();
          
          // Check for JWT expired error
          if (error.includes('JWT expired') || error.includes('PGRST301')) {
            // JWT expired in RPC, clearing session
            // Clear the expired session
            localStorage.removeItem('supabase-auth-token');
            localStorage.removeItem('supabase-refresh-token');
            localStorage.removeItem('supabase-user');
            localStorage.removeItem('supabase-auth-timestamp');
            sessionStorage.removeItem('supabase-auth-token');
            sessionStorage.removeItem('supabase-refresh-token');
            sessionStorage.removeItem('supabase-user');
            sessionStorage.removeItem('supabase-auth-timestamp');
            
            // Only redirect if we're not already on the login page
            if (!window.location.pathname.includes('/login')) {
              // Check if user is in client portal and redirect appropriately
              if (window.location.pathname.includes('/client-portal')) {
                window.location.href = '/client-portal/login';
              } else {
                window.location.href = '/login';
              }
            }
            return { data: null, error: new Error('Session expired. Please login again.') };
          }
          
          return { data: null, error: new Error(error) };
        }
        
        // Handle empty responses (VOID returns from functions)
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        
        // If response is empty or has no content, return null data
        if (contentLength === '0' || !contentType?.includes('application/json')) {
          return { data: null, error: null };
        }
        
        const data = await response.json();
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    
    channel: (channelName: string) => {
      // Mock implementation for channels (real-time features)
      // Real-time features not supported in minimal client
      const mockSubscription = {
        unsubscribe: () => {
          // Mock channel unsubscribed
        }
      };
      
      return {
        on: (event: string, options: any, callback?: any) => {
          // Handle both signatures: on(event, callback) and on(event, options, callback)
          const actualCallback = callback || options;
          return {
            subscribe: () => {
              // Real-time subscriptions not supported in minimal client - silent return
              return mockSubscription;
            }
          };
        },
        subscribe: () => mockSubscription
      };
    },
    
    functions: {
      invoke: async (functionName: string, options?: { body?: any; headers?: any }) => {
        try {
          const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
          
          const headers: any = {
            'Content-Type': 'application/json',
            'apikey': key,
            'Authorization': token ? `Bearer ${token}` : `Bearer ${key}`,
            ...options?.headers
          };
          
          const response = await fetch(`${url}/functions/v1/${functionName}`, {
            method: 'POST',
            headers,
            body: options?.body ? JSON.stringify(options.body) : undefined
          });
          
          if (!response.ok) {
            const error = await response.text();
            return { data: null, error: new Error(error) };
          }
          
          // Handle empty responses
          const contentType = response.headers.get('content-type');
          const contentLength = response.headers.get('content-length');
          
          if (contentLength === '0' || !contentType?.includes('application/json')) {
            return { data: null, error: null };
          }
          
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { data: null, error };
        }
      }
    },
    
    storage: {
      from: (bucket: string) => ({
        upload: async (path: string, file: File | Blob, options?: any) => {
          try {
            const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
            if (!token) {
              return { data: null, error: new Error('Not authenticated') };
            }
            
            const formData = new FormData();
            formData.append('file', file);
            
            // Add cache control if provided
            const headers: any = {
              'apikey': key,
              'Authorization': `Bearer ${token}`
            };
            
            if (options?.cacheControl) {
              headers['cache-control'] = options.cacheControl;
            }
            
            const response = await fetch(`${url}/storage/v1/object/${bucket}/${path}`, {
              method: 'POST',
              headers,
              body: file
            });
            
            if (!response.ok) {
              const error = await response.text();
              return { data: null, error: new Error(error) };
            }
            
            const data = await response.json();
            return { data: { path, id: data.Id, fullPath: `${bucket}/${path}` }, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
        
        download: async (path: string) => {
          try {
            const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
            if (!token) {
              return { data: null, error: new Error('Not authenticated') };
            }
            
            const response = await fetch(`${url}/storage/v1/object/${bucket}/${path}`, {
              headers: {
                'apikey': key,
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!response.ok) {
              const error = await response.text();
              return { data: null, error: new Error(error) };
            }
            
            const blob = await response.blob();
            return { data: blob, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
        
        list: async (path?: string, options?: any) => {
          try {
            const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
            if (!token) {
              return { data: null, error: new Error('Not authenticated') };
            }
            
            const params = new URLSearchParams();
            if (options?.limit) params.append('limit', options.limit.toString());
            if (options?.offset) params.append('offset', options.offset.toString());
            
            const prefix = path || '';
            const response = await fetch(`${url}/storage/v1/object/list/${bucket}?prefix=${encodeURIComponent(prefix)}&${params}`, {
              headers: {
                'apikey': key,
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!response.ok) {
              const error = await response.text();
              return { data: null, error: new Error(error) };
            }
            
            const data = await response.json();
            return { data, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
        
        remove: async (paths: string[]) => {
          try {
            const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
            if (!token) {
              return { data: null, error: new Error('Not authenticated') };
            }
            
            const response = await fetch(`${url}/storage/v1/object/${bucket}`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'apikey': key,
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ prefixes: paths })
            });
            
            if (!response.ok) {
              const error = await response.text();
              return { data: null, error: new Error(error) };
            }
            
            const data = await response.json();
            return { data, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
        
        createSignedUrl: async (path: string, expiresIn: number) => {
          try {
            const token = localStorage.getItem('supabase-auth-token') || sessionStorage.getItem('supabase-auth-token');
            if (!token) {
              return { data: null, error: new Error('Not authenticated') };
            }
            
            const response = await fetch(`${url}/storage/v1/object/sign/${bucket}/${path}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': key,
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ expiresIn })
            });
            
            if (!response.ok) {
              const error = await response.text();
              return { data: null, error: new Error(error) };
            }
            
            const data = await response.json();
            return { data: { signedUrl: `${url}/storage/v1${data.signedURL}` }, error: null };
          } catch (error) {
            return { data: null, error };
          }
        },
        
        getPublicUrl: (path: string) => {
          return {
            data: {
              publicUrl: `${url}/storage/v1/object/public/${bucket}/${path}`
            }
          };
        }
      })
    }
  };
  
  return supabaseClient;
}

// Export a default instance
export const supabaseMinimal = getMinimalSupabase();